import React, { useCallback, useMemo } from "react";
import { format } from "date-fns";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { FinancialAccount } from "@common/types/financialAccount";
import { DocumentCategory } from "@common/types/documentCategory";
import { Entity } from "@common/types/entity";
import { getLeadingAccountConnection } from "@common/helpers/financialAccount";
import { currencyFormatter } from "@common/helpers/currency";
import { fullDateLongFormat } from "@common/helpers/date";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { EntityDisplay } from "@components/entity/EntityDisplay";
import { AccountCard } from "@components/financialAccount/AccountCard/AccountCard";
import { AccountPickerItem } from "@components/financialAccount/AccountPickerItem";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { DescriptionList } from "@design-system/DescriptionList/DescriptionList";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useEntities } from "@hooks/useEntities";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { addTimeZoneOffset, formatDateForTransaction } from "@helpers/date";
import {
    useUpdateDocumentCategoryMutation,
    useChangeEntityMutation,
    useChangeFinancialAccountMutation,
} from "@mutations/financialDocument";
import { DocumentStatusList } from "../DocumentStatusList";
import { useDocumentStatusNavigation } from "../useDocumentStatusNavigation";
import { DocumentCategoryCombobox } from "../DocumentCategorySelect/DocumentCategoryCombobox";
import {
    getImportStatus,
    getReconciliationStatus,
    getMatchStatus,
    hasTransactionMatches,
    getDocumentLockedTooltip,
} from "../lib";
import { UploaderDisplay } from "./UploaderDisplay";
import { MetadataItem } from "./MetadataItem";
import { PeriodField } from "./PeriodField";
import styles from "./FinancialDocumentMetadata.module.scss";

interface Props {
    financialDocument: FinancialDocumentExtended;
}

const SELECTOR_BUTTON_PROPS = { variant: "secondary" as const };

const EntityField: React.FC<Readonly<Props>> = ({ financialDocument }) => {
    const entities = useEntities();
    const changeEntity = useChangeEntityMutation();
    const lockedTooltip = getDocumentLockedTooltip(
        financialDocument,
        financialDocument.integrationAccount ?? null,
    );
    const hasMultipleEntities = entities.length > 1;
    const { entity } = financialDocument;
    const entityItems = useMemo(
        () =>
            entities.map((e: Entity) => ({
                value: e.id,
                label: e.name ?? "",
                leading: <EntityAvatar entity={e} size="xs" aria-hidden />,
            })),
        [entities],
    );

    const handleEntityChange = useCallback(
        (entityId: number | null) => {
            if (entityId === null || entityId === entity?.id) {
                return;
            }
            const selected = entities.find((e: Entity) => e.id === entityId);
            if (!selected) {
                return;
            }
            changeEntity.mutate({
                financialDocument,
                newEntity: selected,
            });
        },
        [entities, changeEntity, financialDocument, entity?.id],
    );

    if (hasMultipleEntities && !lockedTooltip) {
        return (
            <MetadataItem label="Entity">
                <Picker.Select
                    items={entityItems}
                    value={entity?.id ?? null}
                    onValueChange={handleEntityChange}
                    placeholder="Select entity"
                    renderValue={() =>
                        entity ? <EntityDisplay entity={entity} /> : undefined
                    }
                    trigger={<Picker.Button variant="secondary" />}
                />
            </MetadataItem>
        );
    }

    if (!entity) {
        return null;
    }

    const content = (
        <span className={styles.inlineIcon}>
            <EntityAvatar entity={entity} size="xs" aria-hidden />
            <BreakableText text={entity.name} showTooltip />
        </span>
    );

    return (
        <MetadataItem label="Entity">
            {lockedTooltip ? (
                <Tooltip content={lockedTooltip}>{content}</Tooltip>
            ) : (
                content
            )}
        </MetadataItem>
    );
};

const AccountField: React.FC<Readonly<Props>> = ({ financialDocument }) => {
    const financialAccounts = useFinancialAccounts();
    const changeAccount = useChangeFinancialAccountMutation();
    const lockedTooltip = getDocumentLockedTooltip(
        financialDocument,
        financialDocument.integrationAccount ?? null,
    );

    const accountsById = useMemo(
        () =>
            new Map<number, FinancialAccount>(
                financialAccounts.map((a: FinancialAccount) => [a.id, a]),
            ),
        [financialAccounts],
    );
    const accountItems = useMemo(
        () =>
            financialAccounts.map((account: FinancialAccount) => (
                <AccountPickerItem key={account.id} account={account} />
            )),
        [financialAccounts],
    );

    const documentAccount = financialDocument.financialAccount;
    const financialAccount = documentAccount
        ? (accountsById.get(documentAccount.id) ?? documentAccount)
        : null;
    const connection = financialAccount
        ? getLeadingAccountConnection(financialAccount)
        : null;

    const handleAccountChange = useCallback(
        (accountId: FinancialAccount["id"] | null) => {
            if (accountId === (financialAccount?.id ?? null)) {
                return;
            }
            const selectedAccount = accountId
                ? (accountsById.get(accountId) ?? null)
                : null;
            if (accountId !== null && !selectedAccount) {
                return;
            }
            changeAccount.mutate({
                financialDocument,
                financialAccount: selectedAccount,
            });
        },
        [accountsById, changeAccount, financialDocument, financialAccount?.id],
    );

    const accountCardContent = financialAccount ? (
        <AccountCard
            account={financialAccount}
            connection={connection ?? undefined}
            onlyNumber
            displayNameAsTooltip
            showType={false}
            iconSize="sm"
        />
    ) : undefined;

    if (lockedTooltip) {
        if (!financialAccount) {
            return null;
        }
        return (
            <MetadataItem label="Account">
                <Tooltip content={lockedTooltip}>
                    <span>{accountCardContent}</span>
                </Tooltip>
            </MetadataItem>
        );
    }

    return (
        <MetadataItem label="Account">
            <Picker.Select
                value={financialAccount?.id ?? null}
                onValueChange={handleAccountChange}
            >
                <Picker.ButtonTrigger
                    render={
                        <Picker.Button
                            variant="secondary"
                            size="md"
                            aria-label={financialAccount?.name ?? undefined}
                        />
                    }
                    value={accountCardContent}
                    placeholder="Select account"
                />
                <Picker.Popup>
                    <Picker.List>
                        {financialAccounts.length === 0 ? (
                            <Picker.Empty>No accounts available</Picker.Empty>
                        ) : (
                            <>
                                {accountItems}
                                {financialAccount && (
                                    <>
                                        <Picker.Separator />
                                        <Picker.Item
                                            onSelect={() =>
                                                handleAccountChange(null)
                                            }
                                        >
                                            Remove account
                                        </Picker.Item>
                                    </>
                                )}
                            </>
                        )}
                    </Picker.List>
                </Picker.Popup>
            </Picker.Select>
        </MetadataItem>
    );
};

export const FinancialDocumentMetadata: React.FC<Props> = ({
    financialDocument,
}) => {
    const statusCallbacks = useDocumentStatusNavigation(financialDocument);
    const updateCategory = useUpdateDocumentCategoryMutation();

    const { amount, date, createdAt, documentCategory } = financialDocument;

    const handleCategoryChange = useCallback(
        (category: DocumentCategory | null) => {
            updateCategory.mutate({
                document: financialDocument,
                documentCategory: category,
            });
        },
        [updateCategory, financialDocument],
    );

    const hasStatus =
        getImportStatus(financialDocument) !== null ||
        getReconciliationStatus(financialDocument) !== null ||
        (hasTransactionMatches(financialDocument) &&
            getMatchStatus(financialDocument) !== null);

    return (
        <DescriptionList>
            <MetadataItem label="Uploaded by">
                <div className={styles.inlineIcon}>
                    <UploaderDisplay
                        uploader={financialDocument.uploader}
                        source={financialDocument.source}
                    />
                    <span>– {formatDateForTransaction(createdAt)}</span>
                </div>
            </MetadataItem>

            <MetadataItem label="Document category">
                <DocumentCategoryCombobox
                    selectedCategory={documentCategory}
                    onChange={handleCategoryChange}
                    triggerButtonProps={SELECTOR_BUTTON_PROPS}
                />
            </MetadataItem>

            <EntityField financialDocument={financialDocument} />

            <AccountField financialDocument={financialDocument} />

            <PeriodField financialDocument={financialDocument} />

            {amount && (
                <MetadataItem label="Amount">
                    {currencyFormatter.format(amount)}
                </MetadataItem>
            )}

            {date && (
                <MetadataItem label="Date">
                    {format(
                        addTimeZoneOffset(new Date(date)),
                        fullDateLongFormat,
                    )}
                </MetadataItem>
            )}

            {hasStatus && (
                <MetadataItem label="Status">
                    <DocumentStatusList
                        document={financialDocument}
                        {...statusCallbacks}
                    />
                </MetadataItem>
            )}
        </DescriptionList>
    );
};
